import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LogoLight from '../../assets/logo-mark.png';
import LogoDark from '../../assets/logo-mark-dark.png';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ExitToApp,
  Person,
  DarkMode,
  LightMode,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode } from '../../context/ThemeContext';
import { ROUTES } from '../../utils/constants';

const Navbar = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const { mode, toggleColorMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    const role = sessionStorage.getItem('active_role');
    let navigationSuccessful = false;
    try {
      if (role === 'admin') {
        navigate('/admin-login');
      } else {
        navigate('/business-login');
      }
      navigationSuccessful = true;
    } catch (err) {
      console.error('Navigation failed:', err);
    }
    if (!navigationSuccessful) {
      if (role === 'admin') {
        window.location.href = '/admin-login';
      } else {
        window.location.href = '/business-login';
      }
    }
    setTimeout(() => {
      logout();
      handleClose();
    }, 0);
  };

  const handleProfile = () => {
    navigate(ROUTES.BUSINESS_PROFILE);
    handleClose();
  };

  const getInitials = () => {
    if (user?.businessName) return user.businessName.trim().charAt(0).toUpperCase();
    if (!user?.fullName) return user?.email?.charAt(0).toUpperCase() || 'U';
    return user.fullName
      .split(' ')
      .map((name) => name.charAt(0))
      .join('')
      .toUpperCase();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 64, sm: 72 } }}>
        {/* Menu Button */}
        <IconButton
          aria-label="open drawer"
          onClick={onMenuClick}
          edge="start"
          sx={{
            mr: { xs: 1, sm: 2 },
            color: 'text.primary',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, minWidth: 0 }}>
          <Box
            onClick={() => navigate(isAdmin ? ROUTES.ADMIN_DASHBOARD : ROUTES.BUSINESS_DASHBOARD)}
            sx={{
              height: { xs: 36, sm: 44 },
              width: { xs: 36, sm: 44 },
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              cursor: 'pointer',
            }}
          >
            <Box
              component="img"
              src={mode === 'dark' ? LogoDark : LogoLight}
              alt="Logo"
              sx={{ height: '100%', width: '100%', objectFit: 'contain' }}
            />
          </Box>
        </Box>

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            aria-label="toggle light and dark mode"
            onClick={toggleColorMode}
            sx={{
              color: 'text.primary',
              backgroundColor: 'action.hover',
              '&:hover': { backgroundColor: 'action.selected' },
            }}
          >
            {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
          </IconButton>

          {/* Role Badge */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              mr: 1,
            }}
          >
            <Typography variant="caption" fontWeight={600}>
              {isAdmin ? 'Admin' : 'Business User'}
            </Typography>
          </Box>

          {/* User Info */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              alignItems: 'flex-end',
              mr: 1,
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }} color="text.primary">
              {user?.businessName || user?.fullName || user?.email?.split('@')[0] || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>

          {/* Profile Avatar */}
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            sx={{ p: 0.5 }}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 700,
                fontSize: '0.95rem',
              }}
            >
              {getInitials()}
            </Avatar>
          </IconButton>
        </Box>

        {/* Profile Menu */}
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          keepMounted
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          sx={{
            mt: 1,
            '& .MuiPaper-root': {
              borderRadius: 1.5,
              minWidth: 220,
              border: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600} color="text.primary">
              {user?.businessName || user?.fullName || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>

          <Divider />

          {!isAdmin && (
            <MenuItem onClick={handleProfile} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Person fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" fontWeight={500}>Profile</Typography>
              </ListItemText>
            </MenuItem>
          )}

          {!isAdmin && <Divider />}

          <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <ExitToApp fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2" fontWeight={600} color="error.main">Logout</Typography>
            </ListItemText>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
